import React from 'react';

import autoBind from 'react-autobind';
import Switch, { Case } from 'react-switch-case';

// import other components within the same project here

import Localization from "../../Localization.jsx"
import { isUnsetCoord } from "../../utils/hotspotGeo.js";
import Config from "../../Config.jsx";
import HotspotViewerPage from "../../pages/HotspotViewerPage.jsx"

import { Button, Tooltip, Modal, Text, Select, Loader, Progress, TextInput,
  Textarea, Checkbox, Chip, Input, Card, Stack, Group, SimpleGrid, CloseButton,
  Switch as MantineSwitch } from '@mantine/core';
import {
  IconArrowLeft, IconLanguage, IconCompass, IconSparkles, IconSettings,
} from '@tabler/icons-react';
import { BRAND_PRIMARY } from "../../theme/brandTheme.js";
import { ContentTypeIcon } from "../content/contentTypeIcon.jsx";

// Default model for the "Generate AI Content Suggestions" feature; users can
// switch to any other configured model (e.g. GPT-5) via the AI Model selector.
const DEFAULT_AI_MODEL = 'gpt-5.4';

// Persisted (per-browser) teacher preference for the AI output language:
// 'auto' follows the selected curriculum; 'tc' / 'en' force the language.
const AI_OUTPUT_LANGUAGE_STORAGE_KEY = 'ev_ai_output_language';

function loadAiOutputLanguagePreference() {
  try {
    const stored = localStorage.getItem(AI_OUTPUT_LANGUAGE_STORAGE_KEY);
    if (stored === 'tc' || stored === 'sc' || stored === 'en' || stored === 'auto') {
      return stored;
    }
  } catch (e) {
    // localStorage unavailable (private mode etc.) — fall through to default.
  }
  return 'auto';
}

class HotspotViewerBar extends React.Component {
  constructor(props) {
    super(props);
    this.refs = React.createRef();

    // state variables here
    this.state = {
      selectedAgent: DEFAULT_AI_MODEL,
      availableModels: [],
      showAiResponseDialog: false,
      waitingAIResponse: false,
      // Inline error shown in the dialog's setup view after a failed generation.
      aiErrorMessage: null,
      // Content types the user wants the AI to suggest (kind ids). Defaults to all
      // suggestible types; an empty selection is treated as "all types".
      aiContentTypeKinds: Config.AI_SUGGESTIBLE_CONTENT_TYPES.slice(),
      // Curriculum grounding for the AI prompt: the KLA -> subject -> level tree
      // (from get_curriculum_options) and the teacher's optional selection.
      curriculumOptions: [],
      selectedCurriculumKla: null,
      selectedCurriculumSubject: null,
      selectedCurriculumLevel: null,
      // Output language for generated suggestions ('auto' | 'tc' | 'en'),
      // restored from the saved per-browser preference.
      aiOutputLanguage: loadAiOutputLanguagePreference(),
      // Editable working copy of the latest parsed AI suggestions.
      aiSuggestions: [],
      // Which suggestion the results view's left nav has selected/highlighted.
      selectedSuggestionIndex: 0,
      // "Translate Locale" feature state.
      showTranslateDialog: false,
      translateTargetLang: 'tc',
      translating: false,
      localePublished: parseInt(
        props.parent && props.parent.props && props.parent.props.currentLocale
          ? props.parent.props.currentLocale.is_ready
          : 0,
        10
      ) > 0,
      publishUpdating: false,
    };
    // DOM handles for the results view's anchored scrolling (left nav card ->
    // corresponding editor card in the right scroll pane). Not state: they are
    // rebuilt on every render and never trigger re-renders themselves.
    this.suggestionCardRefs = {};
    this.suggestionsScrollContainer = null;
    // While a nav-click smooth scroll animates, ignore the container's scroll
    // events so the scroll-position sync doesn't flick through the items in
    // between the old and new selection.
    this.scrollSyncSuppressUntil = 0;
    autoBind(this);
  }

  componentDidMount() {
    // Fetch the configured models (e.g. GPT-5, GPT-5.4) for the AI Model selector.
    this.fetchAvailableModels();
    // Fetch the KLA/subject/level tree for the curriculum selectors. If the
    // backend doesn't provide it (older API), the selectors simply stay hidden.
    this.fetchCurriculumOptions();
  }

  fetchAvailableModels = () => {
    this.props.root.refs.API.request(
      "get_available_azure_models",
      {},
      (res) => {
        if (res.body && res.body.result && Array.isArray(res.body.result)) {
          const models = res.body.result;
          // Default to GPT-5.4 when available, otherwise the first configured model.
          const preferred = models.find((m) => m.model === DEFAULT_AI_MODEL);
          this.setState({
            availableModels: models,
            selectedAgent: preferred ? preferred.model : (models.length > 0 ? models[0].model : DEFAULT_AI_MODEL),
          });
        }
      },
      (err) => {
        console.error("Failed to fetch models", err);
      }
    );
  };

  handleAgentChange = (event) => {
    this.setState({ selectedAgent: event.target.value });
  };

  fetchCurriculumOptions = () => {
    this.props.root.refs.API.request(
      "get_curriculum_options",
      {},
      (res) => {
        const options = res.body && res.body.result;
        if (Array.isArray(options)) {
          this.setState({ curriculumOptions: options });
        }
      },
      (err) => {
        console.error("Failed to fetch curriculum options", err);
      }
    );
  };

  // ---- Curriculum (KLA / subject / level) selection ----

  // Display name for a KLA / subject node, following the current UI language.
  curriculumNodeName(node) {
    const lang = typeof Localization.strings.getLanguage === 'function'
      ? Localization.strings.getLanguage() : 'tc';
    return lang === 'en' ? (node.name_en || node.name_zh) : (node.name_zh || node.name_en);
  }

  // Level keys ("P1-P6", "S4-S6", ...) display as-is except "cross-level".
  curriculumLevelLabel(levelKey) {
    return levelKey === 'cross-level'
      ? Localization.strings.AI_CURRICULUM_CROSS_LEVEL : levelKey;
  }

  getSelectedCurriculumKlaNode() {
    return this.state.curriculumOptions.find((k) => k.key === this.state.selectedCurriculumKla) || null;
  }

  getSelectedCurriculumSubjectNode() {
    const kla = this.getSelectedCurriculumKlaNode();
    if (!kla || !Array.isArray(kla.subjects)) {
      return null;
    }
    return kla.subjects.find((s) => s.key === this.state.selectedCurriculumSubject) || null;
  }

  // Cascading selection: changing a broader field resets the narrower ones, and
  // a single-child list is auto-selected to save the teacher a click.
  handleCurriculumKlaChange(value) {
    const kla = this.state.curriculumOptions.find((k) => k.key === value) || null;
    const onlySubject = (kla && kla.subjects.length === 1) ? kla.subjects[0] : null;
    const onlyLevel = (onlySubject && onlySubject.levels.length === 1) ? onlySubject.levels[0] : null;
    this.setState({
      selectedCurriculumKla: value,
      selectedCurriculumSubject: onlySubject ? onlySubject.key : null,
      selectedCurriculumLevel: onlyLevel ? onlyLevel.key : null,
    });
  }

  handleCurriculumSubjectChange(value) {
    const kla = this.getSelectedCurriculumKlaNode();
    const subject = (kla && kla.subjects.find((s) => s.key === value)) || null;
    const onlyLevel = (subject && subject.levels.length === 1) ? subject.levels[0] : null;
    this.setState({
      selectedCurriculumSubject: value,
      selectedCurriculumLevel: onlyLevel ? onlyLevel.key : null,
    });
  }

  handleCurriculumLevelChange(value) {
    this.setState({ selectedCurriculumLevel: value });
  }

  // Output-language choice doubles as a saved preference so the teacher doesn't
  // have to re-pick it on every generation.
  handleAiOutputLanguageChange(value) {
    const lang = (value === 'tc' || value === 'sc' || value === 'en') ? value : 'auto';
    this.setState({ aiOutputLanguage: lang });
    try {
      localStorage.setItem(AI_OUTPUT_LANGUAGE_STORAGE_KEY, lang);
    } catch (e) {
      // Persisting is best-effort; the in-session choice still applies.
    }
  }

  // Opens the AI suggestion dialog. The dialog starts in its "setup" view (options
  // + "Start" button); generation no longer fires automatically on open.
  // This component is mounted once and shared across hotspots, so always clear any
  // previous suggestions / error first — otherwise opening the dialog on a new
  // hotspot would show stale results from a prior one.
  handleShowAiResponseDialog = () => {
    this.setState({ showAiResponseDialog: true, aiSuggestions: [], aiErrorMessage: null });
  };

  handleCloseAiResponseDialog = () => {
    // Keep the dialog open while a request is in flight so the progress bar stays
    // visible and the in-flight request isn't visually orphaned.
    if (this.state.waitingAIResponse) {
      return;
    }
    this.setState({ showAiResponseDialog: false });
  };

  // Clears the current suggestions to return the dialog to its setup view so the
  // user can tweak the options and generate again.
  handleResetAiSuggestions = () => {
    this.setState({ aiSuggestions: [], aiErrorMessage: null });
  };

  // The dialog's "Start AI Suggestions" button: gather the locale / hotspot names
  // and fire the generation request.
  handleStartAiGeneration() {
    if (!this.props.parent.state.currentHotspot) {
      console.log("No hotspot selected");
      return;
    }

    // Get locale name from the parent component
    const localeName = this.props.parent.getCurrentLocaleName ?
      this.props.parent.getCurrentLocaleName() : "";

    // Get hotspot name from the editable textfield in hotspotDetailView
    let hotspotName = "";
    if (this.props.parent.refs.hotspotDetailView) {
      hotspotName = this.props.parent.refs.hotspotDetailView.state.hotspotName;
    } else {
      hotspotName = this.props.parent.state.currentHotspot ? this.props.parent.state.currentHotspot.name : "";
    }

    this.askAI(localeName, hotspotName);
  }

  // Build the list of content types (kind + localized title/description) that the
  // LLM is allowed to choose from, so the backend prompt can reference them.
  // Honours the user's selection in the dialog; an empty selection means "all".
  getSuggestibleContentTypes() {
    const selected = this.state.aiContentTypeKinds;
    const kinds = (Array.isArray(selected) && selected.length > 0)
      ? Config.AI_SUGGESTIBLE_CONTENT_TYPES.filter((kind) => selected.includes(kind))
      : Config.AI_SUGGESTIBLE_CONTENT_TYPES;
    return kinds.map((kind) => {
      const cfg = Config.GET_HOTSPOT_CONTENT_CONFIG(kind);
      return { kind, title: cfg.title, description: cfg.description };
    });
  }

  askAI(localeName = "", hotspotName = "") {
    const requestBody = {
      locale_name: localeName,
      hotspot_name: hotspotName,
      ai_agent: "azure",
      model: this.state.selectedAgent || DEFAULT_AI_MODEL,
      content_types: this.getSuggestibleContentTypes(),
      // 'auto' lets the backend follow the selected curriculum; 'tc'/'en' force it.
      output_language: this.state.aiOutputLanguage,
    };

    // Curriculum grounding is only meaningful as a complete path — the backend
    // resolves documents by (KLA, subject, level); partial selections are ignored.
    if (this.state.selectedCurriculumKla &&
        this.state.selectedCurriculumSubject &&
        this.state.selectedCurriculumLevel) {
      requestBody.curriculum_kla = this.state.selectedCurriculumKla;
      requestBody.curriculum_subject = this.state.selectedCurriculumSubject;
      requestBody.curriculum_level = this.state.selectedCurriculumLevel;
    }

    this.props.root.refs.API.request(
      "get_AI_response",
      requestBody,
      this.getAIResponseOK,
      this.getAIResponseFail
    );

    this.setState({ waitingAIResponse: true, aiErrorMessage: null, aiSuggestions: [] });
  }

  getAIResponseOK(res) {
    console.log("Response from server:", res);

    const aiResponse = res.body?.result?.ai_response;
    // If aiResponse is missing or is an object with an error, treat as error
    if (!aiResponse || (typeof aiResponse === 'object' && aiResponse !== null && aiResponse.error)) {
      const errorMsg = (typeof aiResponse === 'object' && aiResponse !== null) ? aiResponse.error : 'No AI response received';
      this.getAIResponseFail(errorMsg);
      return;
    }

    const rawText = Array.isArray(aiResponse) ? aiResponse.join('') : String(aiResponse);
    const suggestions = this.parseSuggestions(rawText);

    // null => could not parse JSON; [] => parsed but nothing usable.
    if (suggestions === null) {
      this.notifyAiError(Localization.strings.AI_PARSE_ERROR);
      return;
    }
    if (suggestions.length === 0) {
      this.notifyAiError(Localization.strings.AI_NO_SUGGESTIONS);
      return;
    }

    // The dialog is already open (showing the progress bar); just swap it to the
    // results view by populating the suggestions.
    this.setState({
      waitingAIResponse: false,
      aiErrorMessage: null,
      aiSuggestions: suggestions,
      selectedSuggestionIndex: 0,
    });
  }

  // Stops the progress bar and surfaces the error inline in the dialog's setup
  // view (and as a toast), so the user can adjust the options and try again.
  notifyAiError(message) {
    const text = message || Localization.strings.AI_GENERATE_FAILED;
    this.setState({ waitingAIResponse: false, aiErrorMessage: text });
    if (this.props.root && typeof this.props.root.addNotification === 'function') {
      this.props.root.addNotification(text, false);
    }
  }

  getAIResponseFail(error) {
    console.error("Error calling server API:", error);
    this.notifyAiError(Localization.strings.AI_GENERATE_FAILED);
  }

  // Parse the fixed-format JSON response into editable suggestion objects.
  // Returns null when the payload can't be parsed as JSON (so callers can show a
  // distinct "couldn't read" error), or an array (possibly empty) of suggestions.
  parseSuggestions(rawText) {
    if (typeof rawText !== 'string') {
      return null;
    }

    let text = rawText.trim();

    // Strip a leading/trailing markdown code fence if the model added one.
    if (text.startsWith('```')) {
      text = text.replace(/^```[a-zA-Z]*\s*/, '').replace(/```\s*$/, '').trim();
    }

    // If there is surrounding prose, keep only the outermost JSON object.
    const firstBrace = text.indexOf('{');
    const lastBrace = text.lastIndexOf('}');
    if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
      text = text.slice(firstBrace, lastBrace + 1);
    }

    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch (e) {
      console.error("Failed to parse AI suggestions JSON:", e, rawText);
      return null;
    }

    const list = Array.isArray(parsed?.suggestions)
      ? parsed.suggestions
      : (Array.isArray(parsed) ? parsed : []);

    const allowed = Config.AI_SUGGESTIBLE_CONTENT_TYPES;
    const result = [];

    list.forEach((s) => {
      if (!s || typeof s !== 'object') {
        return;
      }

      let kind = parseInt(s.content_type, 10);
      if (Number.isNaN(kind) || !allowed.includes(kind)) {
        // Unknown/unsupported type -> fall back to a plain description.
        kind = Config.HOTSPOT_CONTENT_SIMPLE_DESCRIPTION;
      }

      const rawChoices = Array.isArray(s.choices) ? s.choices : [];
      const rawAnswers = Array.isArray(s.answers) ? s.answers : [];
      const rawColumns = Array.isArray(s.columns) ? s.columns : [];
      const rawRows = Array.isArray(s.rows) ? s.rows : [];
      // Table headers are stored as comma-separated strings, so strip any commas
      // inside a cell and drop empties before joining.
      const joinCells = (arr) => arr
        .map((c) => (c == null ? '' : String(c).replace(/,/g, ' ').trim()))
        .filter((c) => c !== '')
        .join(',');

      result.push({
        include: true,
        kind,
        name: s.name != null ? String(s.name) : '',
        content: s.content != null ? String(s.content) : '',
        // Normalise to exactly 4 choices (choices[0] is the correct answer for MC).
        choices: [0, 1, 2, 3].map((i) => (rawChoices[i] != null ? String(rawChoices[i]) : '')),
        // Normalise to 2 answers for fill-in-the-blank editing.
        answers: [0, 1].map((i) => (rawAnswers[i] != null ? String(rawAnswers[i]) : '')),
        // Table (kind 12) headers, edited as comma-separated text.
        column_names: joinCells(rawColumns),
        row_names: joinCells(rawRows),
        // Marking scheme (kinds 8 and 13) for later AI assessment.
        marking_correct: s.marking_correct != null ? String(s.marking_correct) : '',
        marking_exclude: s.marking_exclude != null ? String(s.marking_exclude) : '',
        marking_guidelines: s.marking_guidelines != null ? String(s.marking_guidelines) : '',
      });
    });

    return result;
  }

  // ---- Suggestion editing helpers ----

  updateSuggestionField(index, field, value) {
    this.setState((prev) => {
      const next = prev.aiSuggestions.slice();
      next[index] = { ...next[index], [field]: value };
      return { aiSuggestions: next };
    });
  }

  toggleSuggestionInclude(index) {
    this.setState((prev) => {
      const next = prev.aiSuggestions.slice();
      next[index] = { ...next[index], include: !next[index].include };
      return { aiSuggestions: next };
    });
  }

  updateSuggestionChoice(index, choiceIndex, value) {
    this.setState((prev) => {
      const next = prev.aiSuggestions.slice();
      const choices = next[index].choices.slice();
      choices[choiceIndex] = value;
      next[index] = { ...next[index], choices };
      return { aiSuggestions: next };
    });
  }

  updateSuggestionAnswer(index, answerIndex, value) {
    this.setState((prev) => {
      const next = prev.aiSuggestions.slice();
      const answers = next[index].answers.slice();
      answers[answerIndex] = value;
      next[index] = { ...next[index], answers };
      return { aiSuggestions: next };
    });
  }

  // ---- Submission ----

  // Collapse a comma-separated string into clean, single-comma-delimited headers
  // (no leading/trailing/double commas), matching the table content structure.
  normalizeCommaList(value) {
    return String(value || '')
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s !== '')
      .join(',');
  }

  // Build a `component` insert request matching the existing content structure
  // (mirrors CreateContentDialog.handleFinishClicked).
  buildContentRow(suggestion, displayIndex, epackId, hotspotId) {
    let payload = {};
    switch (suggestion.kind) {
      case Config.HOTSPOT_CONTENT_MC_OF_TEXTS:
        // ans0 is the correct answer; ans1-3 are distractors.
        payload = {
          ans0: suggestion.choices[0] || '',
          ans1: suggestion.choices[1] || '',
          ans2: suggestion.choices[2] || '',
          ans3: suggestion.choices[3] || '',
        };
        break;
      case Config.HOTSPOT_CONTENT_FILL_IN_THE_BLANK:
        payload = {
          text1: suggestion.answers[0] || '',
          text2: suggestion.answers[1] || '',
        };
        break;
      case Config.HOTSPOT_CONTENT_TABLE_AND_CELL:
        payload = {
          column_names: this.normalizeCommaList(suggestion.column_names),
          row_names: this.normalizeCommaList(suggestion.row_names),
        };
        break;
      case Config.HOTSPOT_CONTENT_QUESTION:
      case Config.HOTSPOT_CONTENT_TAKE_RECORD:
        // Marking scheme for later AI assessment — same camelCase payload keys
        // that ContentDetailView.markingScheme() edits and the AI assessment
        // backend reads (correctAnswer / answerToExclude / otherGuidelines).
        payload = {
          correctAnswer: suggestion.marking_correct || '',
          answerToExclude: suggestion.marking_exclude || '',
          otherGuidelines: suggestion.marking_guidelines || '',
        };
        break;
      default:
        // Photo-taking (11) and Drawing (17) tasks only use `content` (the task prompt).
        payload = {};
    }

    return {
      table: "component",
      row: {
        name: suggestion.name || '',
        kind: suggestion.kind,
        display_index: displayIndex,
        diff: 1,
        epack_id: epackId,
        hotspot_id: hotspotId,
        content: suggestion.content || '',
        payload: JSON.stringify(payload),
        score_max: -1,
      },
    };
  }

  handleAddSelectedSuggestions() {
    const selected = this.state.aiSuggestions.filter((s) => s.include);
    if (selected.length === 0) {
      this.props.root.addNotification(Localization.strings.AI_NO_SUGGESTION_SELECTED, false);
      return;
    }

    const hotspotDetailView = this.props.parent.refs.hotspotDetailView;
    const currentList = (hotspotDetailView && hotspotDetailView.state.contentList)
      ? hotspotDetailView.state.contentList : [];

    // Enforce the per-hotspot component cap (same rule as the manual "Add
    // Content" button in HotspotDetailView.handleAddContentClicked).
    const userDataProfile = this.props.root.refs.userDataProfile;
    const userRole = (userDataProfile && userDataProfile.state.userInfo)
      ? userDataProfile.state.userInfo.role : 0;
    const maxComponents = (userRole == 2)
      ? Config.MAX_COMPONENET_LEN().PREMIUM
      : Config.MAX_COMPONENET_LEN().NORMAL;
    if (currentList.length + selected.length > maxComponents) {
      const remaining = Math.max(maxComponents - currentList.length, 0);
      this.props.root.addNotification(
        Localization.strings.AI_EXCEED_MAX_COMPONENT
          .replace('{0}', currentList.length)
          .replace('{1}', maxComponents)
          .replace('{2}', remaining),
        false
      );
      return;
    }

    let maxDisplayIndex = -1;
    currentList.forEach((row) => {
      const di = parseInt(row.display_index, 10);
      if (!Number.isNaN(di) && di > maxDisplayIndex) {
        maxDisplayIndex = di;
      }
    });

    const epackId = this.props.root.getCurrentLocale().id;
    const hotspotId = this.props.parent.state.currentHotspot.id;

    let pending = selected.length;
    let succeeded = 0;
    const finalize = () => {
      pending -= 1;
      if (pending > 0) {
        return;
      }
      if (hotspotDetailView && typeof hotspotDetailView.refreshContentList === 'function') {
        hotspotDetailView.refreshContentList();
      }
      this.props.root.refs.API.request("touch_epack", { epack_id: epackId });
      this.props.root.addNotification(
        Localization.strings.AI_SUGGESTIONS_ADDED.replace('{0}', succeeded),
        succeeded > 0
      );
      // Consume the suggestions so reopening the dialog starts fresh at setup.
      this.setState({ showAiResponseDialog: false, aiSuggestions: [], aiErrorMessage: null });
    };

    selected.forEach((suggestion, i) => {
      const requestObj = this.buildContentRow(suggestion, maxDisplayIndex + 1 + i, epackId, hotspotId);
      this.props.root.refs.API.request(
        "insert",
        requestObj,
        () => { succeeded += 1; finalize(); },
        (err) => { console.error("insert AI content failed", err); finalize(); }
      );
    });
  }

  contentLabelForKind(kind) {
    if (kind === Config.HOTSPOT_CONTENT_MC_OF_TEXTS || kind === Config.HOTSPOT_CONTENT_QUESTION) {
      return Localization.strings.DESCRIPTION_QUESTION;
    }
    return Localization.strings.AI_SUGGESTION_CONTENT;
  }

  // Left-nav card click: highlight it and scroll the right pane to the matching
  // editor card (anchor-link style). The scroll container is position:relative,
  // so each card's offsetTop is already relative to it.
  handleSuggestionNavClick(index) {
    this.setState({ selectedSuggestionIndex: index });
    const container = this.suggestionsScrollContainer;
    const card = this.suggestionCardRefs ? this.suggestionCardRefs[index] : null;
    if (container && card) {
      this.scrollSyncSuppressUntil = Date.now() + 700;
      container.scrollTo({ top: Math.max(card.offsetTop - 4, 0), behavior: 'smooth' });
    }
  }

  // Keep the left menu's highlight in sync while the user scrolls the right
  // pane: the active item is the last card whose top has passed the viewport
  // top (with a small tolerance); at the very bottom it's the last card.
  handleSuggestionsScroll() {
    if (Date.now() < this.scrollSyncSuppressUntil) {
      return;
    }
    const container = this.suggestionsScrollContainer;
    const count = this.state.aiSuggestions.length;
    if (!container || count === 0) {
      return;
    }
    let active = 0;
    for (let i = 0; i < count; i++) {
      const card = this.suggestionCardRefs[i];
      if (card && card.offsetTop <= container.scrollTop + 40) {
        active = i;
      }
    }
    if (container.scrollTop + container.clientHeight >= container.scrollHeight - 2) {
      active = count - 1;
    }
    if (active !== this.state.selectedSuggestionIndex) {
      this.setState({ selectedSuggestionIndex: active });
    }
  }

  // A compact nav entry styled like the hotspot detail page's content rows
  // (include-checkbox + type icon + title, no delete button); brand-highlighted
  // when selected, dimmed when the suggestion is unticked.
  renderSuggestionNavCard(suggestion, index) {
    const cfg = Config.GET_HOTSPOT_CONTENT_CONFIG(suggestion.kind);
    const selected = this.state.selectedSuggestionIndex === index;
    const title = suggestion.name !== '' ? suggestion.name : cfg.title;
    return (
      <div
        key={index}
        onClick={() => this.handleSuggestionNavClick(index)}
        style={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          padding: '8px 12px',
          border: '1px solid ' + (selected ? BRAND_PRIMARY : '#eeeeee'),
          backgroundColor: selected ? 'rgba(173, 17, 121, 0.06)' : 'white',
          borderRadius: '8px',
          opacity: suggestion.include ? 1 : 0.55,
        }}
      >
        {/* Wrapping span stops the row's nav-click so ticking the box only
            toggles inclusion without also scrolling the right pane. */}
        <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', marginRight: 10 }}>
          <Checkbox
            size="sm"
            checked={suggestion.include}
            onChange={() => this.toggleSuggestionInclude(index)}
          />
        </span>
        <div style={{ minWidth: '30px', display: 'flex', alignItems: 'center' }}>
          <ContentTypeIcon ligature={cfg.icon} size={22} color={selected ? BRAND_PRIMARY : '#666'} />
        </div>
        <span style={{
          flex: 1,
          minWidth: 0,
          fontSize: '0.9rem',
          fontWeight: 500,
          color: selected ? BRAND_PRIMARY : undefined,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
          {title}
        </span>
      </div>
    );
  }

  // "KLA / subject / level — AI suggested questions" heading for the results
  // view, built from the options the teacher chose; plain "AI suggested
  // questions" when no curriculum was selected.
  buildResultsTitle() {
    const parts = [];
    const kla = this.getSelectedCurriculumKlaNode();
    const subject = this.getSelectedCurriculumSubjectNode();
    if (kla) {
      parts.push(this.curriculumNodeName(kla));
    }
    if (subject) {
      parts.push(this.curriculumNodeName(subject));
    }
    if (subject && this.state.selectedCurriculumLevel) {
      parts.push(this.curriculumLevelLabel(this.state.selectedCurriculumLevel));
    }
    const suffix = Localization.strings.AI_SUGGESTED_QUESTIONS;
    return parts.length > 0 ? parts.join(' / ') + ' — ' + suffix : suffix;
  }

  renderSuggestionCard(suggestion, index) {
    const cfg = Config.GET_HOTSPOT_CONTENT_CONFIG(suggestion.kind);
    return (
      <Card
        key={index}
        withBorder
        padding="md"
        style={{
          margin: '12px 0',
          // Unticked in the left menu -> grayed out and non-interactive here;
          // re-enable via the left menu's checkbox.
          opacity: suggestion.include ? 1 : 0.45,
          pointerEvents: suggestion.include ? 'auto' : 'none',
          backgroundColor: suggestion.include ? undefined : '#fafafa',
        }}
      >
        <Stack gap="xs">
          {/* Content type shown as icon + title (matching the hotspot content
              list) instead of a dropdown — the type is fixed by the AI. */}
          <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
            <ContentTypeIcon ligature={cfg.icon} size={24} color={BRAND_PRIMARY} />
            <Text fw={600} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {cfg.title}
            </Text>
          </Group>

          <TextInput
            label={Localization.strings.TITLE}
            value={suggestion.name}
            onChange={(e) => this.updateSuggestionField(index, 'name', e.target.value)}
          />

          <Textarea
            autosize
            minRows={2}
            label={this.contentLabelForKind(suggestion.kind)}
            value={suggestion.content}
            onChange={(e) => this.updateSuggestionField(index, 'content', e.target.value)}
          />

          {suggestion.kind === Config.HOTSPOT_CONTENT_MC_OF_TEXTS && (
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs" verticalSpacing="xs">
              {[0, 1, 2, 3].map((ci) => (
                <TextInput
                  key={ci}
                  label={Localization.strings.CHOICE + ' ' + (ci + 1) +
                    (ci === 0 ? ' (' + Localization.strings.CORRECT_ANSWER + ')' : '')}
                  value={suggestion.choices[ci]}
                  onChange={(e) => this.updateSuggestionChoice(index, ci, e.target.value)}
                />
              ))}
            </SimpleGrid>
          )}

          {suggestion.kind === Config.HOTSPOT_CONTENT_FILL_IN_THE_BLANK && (
            <Stack gap="xs">
              {[0, 1].map((ai) => (
                <TextInput
                  key={ai}
                  label={Localization.strings.AI_FILL_ANSWER.replace('{0}', ai + 1)}
                  value={suggestion.answers[ai]}
                  onChange={(e) => this.updateSuggestionAnswer(index, ai, e.target.value)}
                />
              ))}
            </Stack>
          )}

          {suggestion.kind === Config.HOTSPOT_CONTENT_TABLE_AND_CELL && (
            /* Read-only: the header lists are comma-encoded in the payload, so
               free-form edits could produce undecodable table data. Teachers can
               fine-tune the table in the content editor after adding. */
            <Stack gap="xs">
              <TextInput
                label={Localization.strings.AI_TABLE_COLUMNS}
                value={suggestion.column_names}
                readOnly
                variant="filled"
              />
              <TextInput
                label={Localization.strings.AI_TABLE_ROWS}
                value={suggestion.row_names}
                readOnly
                variant="filled"
              />
            </Stack>
          )}

          {(suggestion.kind === Config.HOTSPOT_CONTENT_QUESTION ||
            suggestion.kind === Config.HOTSPOT_CONTENT_TAKE_RECORD) && (
            <Stack gap="xs">
              <Text fw={600} size="sm" mt={4}>{Localization.strings.MARKING_SCHEME}</Text>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs" verticalSpacing="xs">
                {/* Same cap as ContentDetailView.markingScheme(): buildContentRow()
                    writes these into the identical payload keys, so the two entry
                    points must agree or a scheme typed here would be rejected only
                    once it reached the server. */}
                <Textarea
                  autosize
                  minRows={2}
                  label={Localization.strings.CORRECT_ANSWER}
                  value={suggestion.marking_correct}
                  onChange={(e) => this.updateSuggestionField(index, 'marking_correct', e.target.value)}
                  maxLength={Config.MAX_TEXT_LEN().MARKING_SCHEME}
                />
                <Textarea
                  autosize
                  minRows={2}
                  label={Localization.strings.ANSWER_TO_EXCLUDE}
                  value={suggestion.marking_exclude}
                  onChange={(e) => this.updateSuggestionField(index, 'marking_exclude', e.target.value)}
                  maxLength={Config.MAX_TEXT_LEN().MARKING_SCHEME}
                />
              </SimpleGrid>
              <Textarea
                autosize
                minRows={2}
                label={Localization.strings.OTHER_GUIDELINES}
                value={suggestion.marking_guidelines}
                onChange={(e) => this.updateSuggestionField(index, 'marking_guidelines', e.target.value)}
                maxLength={Config.MAX_TEXT_LEN().MARKING_SCHEME}
              />
            </Stack>
          )}
        </Stack>
      </Card>
    );
  }

  // ---- Translate Locale ----

  handleOpenLocaleSettings() {
    const localeViewerPage = this.props.root
      && this.props.root.refs
      && this.props.root.refs.localeViewerPage;
    const currentLocale = this.props.parent
      && this.props.parent.props
      && this.props.parent.props.currentLocale;

    if (localeViewerPage && typeof localeViewerPage.showLocaleSettings === 'function') {
      localeViewerPage.showLocaleSettings(currentLocale);
    }
  }

  handlePublishToggle(event) {
    const nextPublished = event.currentTarget.checked;
    const currentLocale = this.props.root.getCurrentLocale();

    if (!currentLocale || currentLocale.id == null || this.state.publishUpdating) {
      return;
    }

    this.setState({
      localePublished: nextPublished,
      publishUpdating: true,
    });

    const requestObj = {
      table: 'epack',
      where_field: 'id',
      where_value: currentLocale.id,
      row: {
        is_ready: nextPublished ? 1 : 0,
      },
    };

    this.props.root.refs.API.request(
      'update',
      requestObj,
      () => {
        const updatedLocale = {
          ...currentLocale,
          is_ready: nextPublished ? 1 : 0,
        };
        this.props.root.setCurrentLocale(updatedLocale);
        this.setState({ publishUpdating: false });
        if (this.props.root.refs.localeViewerPage) {
          this.props.root.refs.localeViewerPage.refreshLocaleList();
        }
        this.props.root.addNotification(Localization.strings.UPDATED, true);
      },
      () => {
        this.setState({
          localePublished: !nextPublished,
          publishUpdating: false,
        });
        this.props.root.addNotification(Localization.strings.UPDATE_FAILED, false);
      }
    );
  }

  handleOpenTranslateDialog() {
    this.setState({ showTranslateDialog: true });
  }

  handleCloseTranslateDialog() {
    // Don't allow closing while a translation request is in flight.
    if (this.state.translating) {
      return;
    }
    this.setState({ showTranslateDialog: false });
  }

  handleTranslateTargetChange(event) {
    this.setState({ translateTargetLang: event.target.value });
  }

  getCurrentEpackId() {
    if (this.props.root && typeof this.props.root.getCurrentLocale === 'function') {
      const locale = this.props.root.getCurrentLocale();
      if (locale && locale.id != null) {
        return locale.id;
      }
    }
    if (this.props.parent && this.props.parent.props && this.props.parent.props.currentLocale) {
      return this.props.parent.props.currentLocale.id;
    }
    return null;
  }

  getTargetLangDisplayName(code) {
    switch (code) {
      case 'tc': return Localization.strings.LANG_TRADITIONAL_CHINESE;
      case 'sc': return Localization.strings.LANG_SIMPLIFIED_CHINESE;
      case 'en': return Localization.strings.LANG_ENGLISH;
      default: return code;
    }
  }

  // Step 1: the language dialog's "Translate" button. Because translation
  // overwrites the locale's text in place, ask the user to confirm (summarising
  // the chosen language) before kicking anything off.
  handleConfirmTranslate() {
    const epackId = this.getCurrentEpackId();
    if (epackId == null) {
      this.props.root.addNotification(Localization.strings.TRANSLATE_FAILED, false);
      return;
    }

    const langName = this.getTargetLangDisplayName(this.state.translateTargetLang);
    const dialogText = {
      title: Localization.strings.TRANSLATE_LOCALE_TITLE,
      content: Localization.strings.TRANSLATE_CONFIRM_PROMPT.replace('{0}', langName),
      confirm: Localization.strings.TRANSLATE_CONFIRM,
      cancel: Localization.strings.CANCEL,
    };
    // The language dialog stays open behind the confirmation, so on cancel the
    // user can change the language or close it.
    this.props.root.refs.confirmDialog.show(
      dialogText,
      () => this.runTranslate(epackId),
      () => {}
    );
  }

  // Step 2: user confirmed — perform the translation.
  runTranslate(epackId) {
    this.setState({ translating: true });

    const requestBody = {
      epack_id: epackId,
      target_language: this.state.translateTargetLang,
      model: this.state.selectedAgent || DEFAULT_AI_MODEL,
    };

    this.props.root.refs.API.request(
      "translate_locale",
      requestBody,
      this.translateLocaleOK,
      this.translateLocaleFail
    );
  }

  translateLocaleOK(res) {
    const result = res && res.body ? res.body.result : null;
    if (!result || result.error) {
      this.translateLocaleFail(result && result.error ? result.error : 'No response');
      return;
    }

    this.setState({ translating: false, showTranslateDialog: false });

    const count = (result.translated_count != null) ? result.translated_count : 0;
    this.props.root.addNotification(
      Localization.strings.TRANSLATE_SUCCESS.replace('{0}', count),
      true
    );

    // Reload the hotspot list so the freshly translated names/content show up.
    const epackId = this.getCurrentEpackId();
    if (epackId != null && this.props.parent && typeof this.props.parent.getHotspotByLocaleID === 'function') {
      this.props.parent.getHotspotByLocaleID(epackId);
    }
    // ...and re-read the epack row itself, which used to be the one thing that
    // never refreshed. translate_locale rewrites epack.name / description / lang
    // in place, but the Locale Name and Description are rendered from the SINGLE
    // cached epack row (TopBar breadcrumb, library card, Locale Settings form),
    // so they kept showing the pre-translation text while the hotspots around
    // them came back translated. Worse, Locale Settings sits in this same
    // toolbar: it seeded its form from that stale row, and pressing UPDATE wrote
    // the original name/description/lang straight back over the translation.
    if (epackId != null) {
      this.refreshTranslatedLocale(epackId);
    }
  }

  // Re-read the just-translated locale. get_epacks_by_ids rather than
  // get_list_epack because it is the only single-row epack read that is not
  // restricted to published locales (GET /api/epack/search always filters
  // epack.is_ready = 1) — a draft being translated has to refresh too.
  refreshTranslatedLocale(epackId) {
    const api = this.props.root.refs ? this.props.root.refs.API : null;
    if (!api) {
      return;
    }
    api.request(
      'get_epacks_by_ids',
      { ids: [epackId] },
      (res) => this.adoptTranslatedLocale(res, epackId),
      (err) => {
        // Non-fatal: the translation itself succeeded and is already in the DB,
        // so keep the success notification and leave the cached row alone rather
        // than guessing at the new text.
        console.warn('translate_locale: could not re-read the locale row', err);
      }
    );
  }

  adoptTranslatedLocale(res, epackId) {
    // API.request routes anything that is not a NEGATIVE result to the OK
    // callback, so `result` can legitimately be null or a non-array here.
    const rows = (res && res.body) ? res.body.result : null;
    const fresh = Array.isArray(rows)
      ? rows.find((row) => row && String(row.id) === String(epackId))
      : null;
    const currentLocale = this.props.root.getCurrentLocale();
    // The user may have navigated elsewhere while the re-read was in flight;
    // adopting the row then would overwrite a different locale's cache entry.
    if (!fresh || !currentLocale || String(currentLocale.id) !== String(epackId)) {
      return;
    }

    // MERGE, do not replace: byIds returns the bare epack row, while the cached
    // one also carries the joined cover (path / tn_path) and the author/school
    // names that the library card renders.
    const updatedLocale = Object.assign({}, currentLocale, fresh);

    const localeViewerPage = this.props.root.refs
      ? this.props.root.refs.localeViewerPage
      : null;
    if (localeViewerPage && typeof localeViewerPage.updateCurrentLocale === 'function') {
      // Updates LocaleViewerPage's own copy as well as RootPage's — the map view
      // reads the page's copy, not root's, so setting only root leaves a second
      // stale row behind.
      localeViewerPage.updateCurrentLocale(updatedLocale);
    } else {
      this.props.root.setCurrentLocale(updatedLocale);
    }

    // The library list still holds the pre-translation card. Patch the row in
    // place FIRST: refreshLocaleList alone is not enough, because a locale that
    // falls outside the current tab/page simply is not in the response and
    // adoptFreshCurrentLocale then keeps the stale row.
    if (localeViewerPage && typeof localeViewerPage.updateLocalLocaleDataById === 'function') {
      localeViewerPage.updateLocalLocaleDataById(epackId, updatedLocale);
    }
    if (localeViewerPage && typeof localeViewerPage.refreshLocaleList === 'function') {
      localeViewerPage.refreshLocaleList();
    }
  }

  translateLocaleFail(err) {
    console.error("translate_locale failed:", err);
    this.setState({ translating: false });
    this.props.root.addNotification(Localization.strings.TRANSLATE_FAILED, false);
  }

  // ---- AI suggestion dialog body (3 views: in-progress / results / setup) ----

  renderAiDialogBody() {
    // 1) Generation in flight: indeterminate (animated) progress bar + text.
    if (this.state.waitingAIResponse) {
      return (
        <Stack gap="lg" py="xl" align="center">
          <Progress
            value={100}
            striped
            animated
            size="lg"
            radius="xl"
            color={BRAND_PRIMARY}
            style={{ width: '100%' }}
          />
          <Group gap="xs" justify="center">
            <Loader size={18} color={BRAND_PRIMARY} />
            <Text c="dimmed">{Localization.strings.AI_GENERATING}</Text>
          </Group>
        </Stack>
      );
    }

    // 2) Results: review-and-edit workflow — left nav of suggestion cards,
    // right scroll pane with the editors, linked like HTML anchors.
    if (this.state.aiSuggestions.length > 0) {
      this.suggestionCardRefs = {};
      return (
        <Stack gap="md">
          <Text fw={600}>{this.buildResultsTitle()}</Text>
          <Group align="stretch" wrap="nowrap" gap="md">
            <Stack gap={6} style={{ width: 230, flexShrink: 0 }}>
              <Text fw={600} size="sm">{Localization.strings.AI_SUGGESTION_INCLUDE}</Text>
              <Stack gap={6} style={{ maxHeight: '58vh', overflowY: 'auto' }}>
                {this.state.aiSuggestions.map((suggestion, index) => this.renderSuggestionNavCard(suggestion, index))}
              </Stack>
            </Stack>
            <div
              ref={(el) => { this.suggestionsScrollContainer = el; }}
              onScroll={this.handleSuggestionsScroll}
              style={{ flex: 1, minWidth: 0, maxHeight: '58vh', overflowY: 'auto', position: 'relative', paddingRight: 4 }}
            >
              {this.state.aiSuggestions.map((suggestion, index) => (
                <div key={index} ref={(el) => { this.suggestionCardRefs[index] = el; }}>
                  {this.renderSuggestionCard(suggestion, index)}
                </div>
              ))}
            </div>
          </Group>
          <Group justify="space-between" mt="sm">
            <Button
              onClick={this.handleResetAiSuggestions}
              variant="subtle"
              leftSection={<IconArrowLeft size={18} />}
            >
              {Localization.strings.AI_CHANGE_OPTIONS}
            </Button>
            <Group gap="sm">
              <Button onClick={this.handleCloseAiResponseDialog} variant="subtle">
                {Localization.strings.CLOSE}
              </Button>
              <Button onClick={this.handleAddSelectedSuggestions}>
                {Localization.strings.AI_ADD_SELECTED}
              </Button>
            </Group>
          </Group>
        </Stack>
      );
    }

    // 3) Setup: prompt-customisation options + "Start" button.
    return (
      <Stack gap="md">
        <Text>{Localization.strings.AI_SETUP_INTRO}</Text>
        {this.state.aiErrorMessage && (
          <Text c="red" size="sm">{this.state.aiErrorMessage}</Text>
        )}
        <Group grow gap="xs">
          <Select
            label={Localization.strings.AI_MODEL}
            allowDeselect={false}
            value={this.state.selectedAgent}
            onChange={(v) => this.handleAgentChange({ target: { value: v } })}
            placeholder={this.state.availableModels.length === 0 ? "Loading..." : undefined}
            data={this.state.availableModels.map((model) => ({ value: model.model, label: model.label }))}
          />
          <Select
            label={Localization.strings.AI_OUTPUT_LANGUAGE}
            allowDeselect={false}
            value={this.state.aiOutputLanguage}
            onChange={this.handleAiOutputLanguageChange}
            data={[
              { value: 'auto', label: Localization.strings.AI_OUTPUT_LANGUAGE_AUTO },
              { value: 'tc', label: Localization.strings.LANG_TRADITIONAL_CHINESE },
              { value: 'sc', label: Localization.strings.LANG_SIMPLIFIED_CHINESE },
              { value: 'en', label: Localization.strings.LANG_ENGLISH },
            ]}
          />
        </Group>
        {this.state.curriculumOptions.length > 0 && (
          <Input.Wrapper description={Localization.strings.AI_CURRICULUM_HINT}>
            <Stack gap="xs" mt={4}>
              <Select
                label={Localization.strings.AI_CURRICULUM_KLA}
                placeholder={Localization.strings.AI_CURRICULUM_NONE}
                clearable
                searchable
                value={this.state.selectedCurriculumKla}
                onChange={this.handleCurriculumKlaChange}
                data={this.state.curriculumOptions.map((kla) => ({
                  value: kla.key,
                  label: this.curriculumNodeName(kla),
                }))}
              />
              <Group grow gap="xs">
                <Select
                  label={Localization.strings.AI_CURRICULUM_SUBJECT}
                  placeholder={Localization.strings.AI_CURRICULUM_NONE}
                  clearable
                  searchable
                  disabled={!this.getSelectedCurriculumKlaNode()}
                  value={this.state.selectedCurriculumSubject}
                  onChange={this.handleCurriculumSubjectChange}
                  data={(this.getSelectedCurriculumKlaNode()?.subjects || []).map((s) => ({
                    value: s.key,
                    label: this.curriculumNodeName(s),
                  }))}
                />
                <Select
                  label={Localization.strings.AI_CURRICULUM_LEVEL}
                  placeholder={Localization.strings.AI_CURRICULUM_NONE}
                  clearable
                  disabled={!this.getSelectedCurriculumSubjectNode()}
                  value={this.state.selectedCurriculumLevel}
                  onChange={this.handleCurriculumLevelChange}
                  data={(this.getSelectedCurriculumSubjectNode()?.levels || []).map((l) => ({
                    value: l.key,
                    label: this.curriculumLevelLabel(l.key),
                  }))}
                />
              </Group>
            </Stack>
          </Input.Wrapper>
        )}
        <Input.Wrapper
          label={Localization.strings.AI_CONTENT_TYPES_TO_GENERATE}
          description={Localization.strings.AI_CONTENT_TYPES_PLACEHOLDER}
        >
          {/* Toggle chips (no dropdown): highlighted when selected, grey when not.
              Defaults to all selected; an empty selection is treated as "all". */}
          <Chip.Group
            multiple
            value={this.state.aiContentTypeKinds.map(String)}
            onChange={(vals) => this.setState({ aiContentTypeKinds: vals.map(Number) })}
          >
            <Group
              gap="xs"
              mt={6}
              p="sm"
              style={{
                border: '1px solid var(--mantine-color-gray-3)',
                borderRadius: 'var(--mantine-radius-sm)',
              }}
            >
              {Config.AI_SUGGESTIBLE_CONTENT_TYPES.map((kind) => (
                <Chip key={kind} value={String(kind)} variant="filled" color={BRAND_PRIMARY} size="sm">
                  {Config.GET_HOTSPOT_CONTENT_CONFIG(kind).title}
                </Chip>
              ))}
            </Group>
          </Chip.Group>
        </Input.Wrapper>
        <Text c="dimmed" size="xs">{Localization.strings.AI_MODEL_WAIT_TIME}</Text>
        <Group justify="flex-end" mt="sm">
          <Button onClick={this.handleCloseAiResponseDialog} variant="subtle">
            {Localization.strings.CLOSE}
          </Button>
          <Button
            onClick={this.handleStartAiGeneration}
            leftSection={<IconSparkles size={18} />}
          >
            {Localization.strings.AI_START_GENERATION}
          </Button>
        </Group>
      </Stack>
    );
  }

  render() {
    let unpositioned = true;
    if (this.props.parent.state.currentHotspot) {
      unpositioned = (isUnsetCoord(this.props.parent.state.currentHotspot.lat) && isUnsetCoord(this.props.parent.state.currentHotspot.lon));
    }
      return (
        <div ref="div" className="bg-color-grey2 padding-std" style={{ flexShrink: 0 }}>
          <Modal
            opened={this.state.showAiResponseDialog}
            onClose={this.handleCloseAiResponseDialog}
            withCloseButton={false}
            padding={0}
            size={this.state.aiSuggestions.length > 0 ? 1050 : 'lg'}
            centered
            zIndex={2000}
            closeOnClickOutside={!this.state.waitingAIResponse}
            closeOnEscape={!this.state.waitingAIResponse}
          >
            <Group
              justify="space-between"
              px="md"
              style={{ height: 56, backgroundColor: BRAND_PRIMARY }}
            >
              <Text fw={600} c="white">{Localization.strings.AI_REVIEW_SUGGESTIONS_TITLE}</Text>
              <CloseButton
                variant="transparent"
                c="white"
                onClick={this.handleCloseAiResponseDialog}
                disabled={this.state.waitingAIResponse}
              />
            </Group>

            <Stack gap="md" p="md">
              {this.renderAiDialogBody()}
            </Stack>
          </Modal>

          <Modal
            opened={this.state.showTranslateDialog}
            onClose={this.handleCloseTranslateDialog}
            title={<Text fw={600}>{Localization.strings.TRANSLATE_LOCALE_TITLE}</Text>}
            size="md"
            centered
            zIndex={2000}
            closeOnClickOutside={!this.state.translating}
            closeOnEscape={!this.state.translating}
            withCloseButton={!this.state.translating}
          >
            <Stack gap="md">
              <Text style={{ marginBottom: 16 }}>
                {Localization.strings.TRANSLATE_SELECT_LANGUAGE}
              </Text>
              <Select
                label={Localization.strings.LANGUAGE}
                disabled={this.state.translating}
                allowDeselect={false}
                value={this.state.translateTargetLang}
                onChange={(v) => this.handleTranslateTargetChange({ target: { value: v } })}
                data={[
                  { value: 'tc', label: Localization.strings.LANG_TRADITIONAL_CHINESE },
                  { value: 'sc', label: Localization.strings.LANG_SIMPLIFIED_CHINESE },
                  { value: 'en', label: Localization.strings.LANG_ENGLISH },
                ]}
              />
              <Text style={{ marginTop: 16, fontSize: '0.85rem', color: '#b71c1c' }}>
                {Localization.strings.TRANSLATE_OVERWRITE_WARNING}
              </Text>
              {this.state.translating && (
                <div style={{ display: 'flex', alignItems: 'center', marginTop: 16 }}>
                  <Loader size={20} style={{ marginRight: 12 }} />
                  <span>{Localization.strings.TRANSLATING}</span>
                </div>
              )}
              <Group justify="flex-end" mt="sm">
                <Button onClick={this.handleCloseTranslateDialog} variant="subtle" disabled={this.state.translating}>
                  {Localization.strings.CANCEL}
                </Button>
                <Button onClick={this.handleConfirmTranslate} disabled={this.state.translating}>
                  {Localization.strings.TRANSLATE_CONFIRM}
                </Button>
              </Group>
            </Stack>
          </Modal>

          <Switch condition={this.props.parent.state.currentView}>
            <Case value={HotspotViewerPage.VIEW_HOTSPOT_LIST()}>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', width: '100%' }}>
              <Button
                variant="default"
                className="margin-sm"
                leftSection={<IconArrowLeft size={18} style={{ color: '#9c27b0' }} />}
                style={{
                  fontWeight: 700,
                  fontSize: '1rem',
                  padding: '8px 20px',
                  borderWidth: '3px',
                  borderColor: '#9c27b0',
                  backgroundColor: 'rgba(156, 39, 176, 0.08)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  color: '#9c27b0'
                }}
                onClick={()=>{
                  if(this.props.parent.hotspotListEmpty()){
                    let dialogText = {
                      title: Localization.strings.EXIT,
                      content: Localization.strings.EMPTY_HOTSPOT_PROMPT,
                      confirm: Localization.strings.YES,
                    };
                    this.props.root.refs.confirmDialog.show(dialogText, ()=>{this.props.root.goBackHistory();}, ()=>{});
                  }else{
                    this.props.root.goBackHistory();
                  }
                }}
              >
                {Localization.strings.LOCALE_LIST}
              </Button>
              <Button
                className="margin-sm"
                leftSection={<IconSettings size={18} />}
                onClick={this.handleOpenLocaleSettings}
              >
                {Localization.strings.LOCALE_SETTINGS}
              </Button>
              <Tooltip label={Localization.strings.PUBLISH_HINT} position="bottom" withArrow>
                <Group
                  className="margin-sm"
                  gap="sm"
                  wrap="nowrap"
                  style={{
                    minHeight: 36,
                    padding: '6px 14px 6px 18px',
                    borderRadius: 999,
                    backgroundColor: BRAND_PRIMARY,
                  }}
                >
                  <Text c="white" fw={600} size="sm">
                    {Localization.strings.PUBLISH}
                  </Text>
                  <MantineSwitch
                    checked={this.state.localePublished}
                    onChange={this.handlePublishToggle}
                    disabled={this.state.publishUpdating}
                    color="green"
                    size="sm"
                    aria-label={Localization.strings.PUBLISH}
                  />
                </Group>
              </Tooltip>
              {/* Spacer pushes TRANSLATE to the right edge. (Can't use
                  marginLeft:auto on the button — .margin-sm sets margin !important.) */}
              <div style={{ flexGrow: 1 }} />
              <Button
                className="margin-sm"
                leftSection={<IconLanguage size={18} />}
                onClick={this.handleOpenTranslateDialog}
              >
                {Localization.strings.TRANSLATE}
              </Button>
              </div>
            </Case>
            <Case value={HotspotViewerPage.VIEW_HOTSPOT_DETAIL()}>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', width: '100%' }}>
                <Button
                  variant="default"
                  className="margin-sm"
                  leftSection={<IconArrowLeft size={18} style={{ color: '#9c27b0' }} />}
                  style={{
                    fontWeight: 700,
                    fontSize: '1rem',
                    padding: '8px 20px',
                    borderWidth: '3px',
                    borderColor: '#9c27b0',
                    backgroundColor: 'rgba(156, 39, 176, 0.08)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    color: '#9c27b0'
                  }}
                  onClick={()=>{
                    let validation_result = this.props.parent.hotspotEditValidation();
                    if(validation_result.flag == 1){
                      let dialogText = {
                        title: Localization.strings.EXIT,
                        content: validation_result.message,
                        confirm: Localization.strings.YES,
                      };
                      this.props.root.refs.confirmDialog.show(dialogText, ()=>{
                        this.props.parent.updateHotspotDetail();
                        this.props.root.goBackHistory();
                      }, ()=>{});
                    }else if (validation_result.flag == 2){
                      this.props.root.addNotification(validation_result.message, false);
                    }else{
                      this.props.parent.updateHotspotDetail();
                      this.props.root.goBackHistory();
                    }
                    }
                  }
                >
                  {Localization.strings.HOTSPOT_LIST}
                </Button>
                <Tooltip label={Localization.strings.MOVE_TO_HOTSPOT_LOCATION_REMINDER} position="bottom-end">
                  <Button
                    className="margin-sm"
                    leftSection={<IconCompass size={18} />}
                    onClick={this.props.parent.teleport}
                    disabled={unpositioned}
                  >
                    {Localization.strings.MOVE_TO_HOTSPOT_LOCATION}
                  </Button>
                </Tooltip>
              </div>
            </Case>
          </Switch>
        </div>
      )
  }
}

export default HotspotViewerBar;
